import { defineStore } from 'pinia'

import {
  FlowScriptData,
  KnockautObject,
  ResolvedKnockautAction,
  SymconFlowScriptData,
  SymconFlowScriptDataAction,
} from '@/apps/brelag/mandator-user/project-editor/knockaut/interfaces'
import { KnockautHandler } from '@/apps/brelag/mandator-user/project-editor/knockaut/knockautHandler'
import {
  getObjectName,
  resolveFormatString,
} from '@/apps/brelag/mandator-user/project-editor/knockaut/util'
import { simpleCopy } from '@/util/util'

export const DefaultFlowScriptData: FlowScriptData = {
  events: [],
  links: [],
  script: null,
  scriptContent: {
    actions: [],
    strategy: {
      error: 0,
      errorRetries: 0,
      executions: 0,
    },
  },
}

export const useFlowScriptEditorStore = defineStore('flowScriptEditor', {
  // arrow function recommended for full type inference
  state: () => {
    return {
      flowScriptObject: null as KnockautObject,
      // This is loaded into Editor (EditKnockautFlowScriptModal.vue)
      flowScriptData: null as FlowScriptData,
      // This is the nicely-formatted actions
      resolvedFlowScriptActions: [] as ResolvedKnockautAction[],
    }
  },
  actions: {
    async resetFlowScriptData(): Promise<void> {
      this.flowScriptData = null
      this.flowScriptObject = null
      this.resolvedFlowScriptActions = []
    },
    async refreshFlowScriptData(): Promise<void> {
      if (this.flowScriptObject) {
        await this.loadFlowScriptData(this.flowScriptObject.id)
      }
    },
    async loadFlowScriptData(id: number): Promise<void> {
      let flowScriptData: FlowScriptData = null
      if (id) {
        flowScriptData = JSON.parse(
          await KnockautHandler.instance.getFlowScriptContent(id)
        )
        this.flowScriptObject = KnockautHandler.instance.getObjectById(id)
      } else {
        flowScriptData = simpleCopy(DefaultFlowScriptData)
        this.flowScriptObject = null
      }
      await this.resolveFlowScriptActions(flowScriptData.scriptContent.actions)
      this.flowScriptData = flowScriptData
    },
    async resolveActionTexts(
      action: ResolvedKnockautAction
    ): Promise<ResolvedKnockautAction> {
      const resolvedAction: ResolvedKnockautAction = simpleCopy(action)
      resolvedAction.targetText = getObjectName(action.parameters.TARGET)
      try {
        resolvedAction.actionText = await resolveFormatString(
          resolvedAction.action,
          resolvedAction.parameters
        )
      } catch (e) {
        resolvedAction.actionText = 'Aktion ist fehlerhaft.'
      }
      return resolvedAction
    },
    async resolveFlowScriptActions(actions: SymconFlowScriptDataAction[]) {
      const resolvedActions: ResolvedKnockautAction[] = []
      for (const [index, action] of actions.entries()) {
        const resolvedAction = await this.resolveActionTexts(action)
        resolvedAction.index = index
        resolvedActions.push(resolvedAction)
      }
      this.resolvedFlowScriptActions = resolvedActions
    },
    async updateFlowScriptLinks(originalLinks: number[], linkIds: number[]) {
      if (this.flowScriptObject) {
        await KnockautHandler.instance.updateLinks(
          originalLinks,
          linkIds,
          this.flowScriptObject.id,
          this.flowScriptObject.name
        )
        await this.refreshFlowScriptData()
      } else {
        throw new Error('Kein Ablaufplan markiert')
      }
    },
    async updateFlowScriptActions() {
      const scriptContent: SymconFlowScriptData = simpleCopy(
        this.flowScriptData.scriptContent
      )
      const updatedActions = []
      for (const action of this.resolvedFlowScriptActions) {
        updatedActions.push({
          actionID: action.actionID,
          parameters: action.parameters,
        })
      }
      scriptContent.actions = updatedActions
      await KnockautHandler.instance.setScriptContent(
        this.flowScriptObject.id,
        JSON.stringify(scriptContent)
      )
    },
    async saveAction(action: ResolvedKnockautAction): Promise<void> {
      const resolvedAction = await this.resolveActionTexts(action)
      if (resolvedAction.index === null) {
        // New action
        resolvedAction.index = this.resolvedFlowScriptActions.length
        this.resolvedFlowScriptActions.push(resolvedAction)
      } else {
        // Update action
        this.resolvedFlowScriptActions[resolvedAction.index] = resolvedAction
      }
    },
    removeAction(action: ResolvedKnockautAction) {
      // Remove action based on index and re-count indexes
      const actions = simpleCopy(this.resolvedFlowScriptActions)
      actions.splice(action.index, 1)
      for (const [index, action] of actions.entries()) {
        action.index = index
      }
      this.resolvedFlowScriptActions = actions
    },
  },
  getters: {
    flowScriptEvents(state) {
      if (state.flowScriptData) {
        return state.flowScriptData.events
      } else {
        return []
      }
    },
    flowScriptLinks(state): number[] {
      if (state.flowScriptData) {
        return state.flowScriptData.links
      } else {
        return []
      }
    },
  },
})
